import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { catchError, forkJoin, of } from 'rxjs';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TimelineModule } from 'primeng/timeline';
import { ToastModule } from 'primeng/toast';
import { CRM_CLIENT_DOCUMENT_TYPES, CRM_REGISTRATION_ATTACHMENT_TYPES, CrmAccount, CrmActivityItem, CrmActivityType, CrmClient, CrmClientAddress, CrmClientCommunication, CrmClientContactDirectoryEntry, CrmClientCreditSnapshot, CrmClientDocument, CrmClientRegistrationAttachment, CrmCommunicationLog, CrmNote, CrmReminder, crmRoleLabel } from '../../crm.model';
import { CrmService } from '../../crm.service';
import { ContractsService } from '../../../contracts/contracts.service';
import { Customer } from '../../../customer/customer.model';
import { CustomerService } from '../../../customer/customer.service';
import { Company } from '../../../company/company.model';
import { CompanyService } from '../../../company/company.service';
import { ContractCrmContext } from '../../../contracts/models/contract-crm-context.model';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PartyQuickCreateDialogComponent, PartyQuickCreateResult } from '../../../shared/party-quick-create/party-quick-create-dialog.component';
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';

type ClientComplianceDocumentSource = 'registration' | 'document';

interface ClientComplianceDocumentRow {
  key: string;
  label: string;
  required: boolean;
  source: ClientComplianceDocumentSource;
  uploads: (CrmClientRegistrationAttachment | CrmClientDocument)[];
}

@Component({
  selector: 'app-client-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    BreadcrumbModule,
    ButtonModule,
    ConfirmDialogModule,
    DatePickerModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ProgressSpinnerModule,
    RadioButtonModule,
    SelectModule,
    TableModule,
    TabsModule,
    TagModule,
    TextareaModule,
    TimelineModule,
    ToastModule,
    PageShellComponent,
    PageDetailHeroComponent,
    PageDataPanelComponent,
    PageMetricStripComponent,
    PartyQuickCreateDialogComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './client-details.component.html',
  styleUrls: ['./client-details.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  public router = inject(Router);
  private crmService = inject(CrmService);
  private contractsService = inject(ContractsService);
  private companyService = inject(CompanyService);
  private customerService = inject(CustomerService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private firebaseStorageService = inject(FirebaseStorageService);
  private sessionStorageService = inject(SessionStorageService);
  public toolbarService = inject(ToolbarService);
  private permissionService = inject(PermissionService);

  client = signal<CrmClient | null>(null);
  address = signal<CrmClientAddress | null>(null);
  masterCommunications = signal<CrmClientCommunication[]>([]);
  contactDirectory = signal<CrmClientContactDirectoryEntry[]>([]);
  documents = signal<CrmClientDocument[]>([]);
  registrationAttachments = signal<CrmClientRegistrationAttachment[]>([]);
  notes = signal<CrmNote[]>([]);
  communications = signal<CrmCommunicationLog[]>([]);
  reminders = signal<CrmReminder[]>([]);
  activities = signal<CrmActivityItem[]>([]);
  contractContext = signal<ContractCrmContext | null>(null);
  creditControl = signal<CrmClientCreditSnapshot | null>(null);
  uploadingDocument = signal(false);
  approvingClient = signal(false);
  linkedCompany = signal<Company | null>(null);
  clientAccounts = signal<CrmAccount[]>([]);
  clientAccountManagers = signal<Customer[]>([]);
  clientLogoLoadFailed = signal(false);
  readonly quickCreateOwningCompany: Company | null = this.sessionStorageService.getCompany() ?? this.toolbarService.customer()?.company ?? null;
  readonly quickCreateCountries = this.sessionStorageService.getCountryList() ?? [];
  quickCreateContactCompany = computed<Company | null>(() => this.linkedCompany() ?? this.quickCreateOwningCompany);

  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  activeTabValue = 0;
  private readonly tabKeys = ['overview', 'tracking', 'contacts', 'accounts', 'documents', 'remarks', 'reminders'] as const;
  breadcrumbItems = signal<MenuItem[]>([
    { label: 'CRM', routerLink: '/crm/clients' },
    { label: 'Clients', routerLink: '/crm/clients' },
    { label: 'Client Details' }
  ]);

  metrics = computed<PageMetric[]>(() => {
    const rows: PageMetric[] = [
      {
        label: 'Finance Approval',
        value: this.approvalStatusLabel(this.client()?.approvalStatus),
        tone: this.client()?.approvalStatus === 'APPROVED' ? 'success' : 'warn'
      },
      { label: 'Business Type', value: this.client()?.businessType || '-' },
      { label: 'Currency', value: this.client()?.currencyCode || '-' },
      { label: 'Client Accounts', value: this.clientAccounts().length, tone: this.clientAccounts().length ? 'info' : 'default' },
      { label: 'Documents', value: this.documents().length + this.registrationAttachments().length, tone: 'info' },
      { label: 'Activities', value: this.activities().length, tone: 'success' }
    ];
    if (this.isCustomerClient()) {
      rows.splice(2, 0, {
        label: 'Credit',
        value: this.creditStatusLabel(this.getCreditStatus()),
        tone: this.creditMetricTone(this.getCreditStatus())
      });
    }
    return rows;
  });

  complianceDocumentRows = computed<ClientComplianceDocumentRow[]>(() => {
    const registrationRows: ClientComplianceDocumentRow[] = this.registrationAttachmentTypes.map(type => ({
      key: type.value,
      label: type.label,
      required: true,
      source: 'registration',
      uploads: this.registrationAttachments().filter(attachment => attachment.category === type.value)
    }));

    const documentRows: ClientComplianceDocumentRow[] = this.documentCategories
      .filter(type => !this.registrationAttachmentTypes.some(registrationType => registrationType.value === type.value))
      .map(type => ({
        key: type.value,
        label: type.label,
        required: type.value === 'CONTRACT',
        source: 'document',
        uploads: this.documents().filter(document => document.category === type.value)
      }));

    return [...registrationRows, ...documentRows];
  });

  requiredComplianceDocumentCount = computed(() => this.complianceDocumentRows().filter(row => row.required).length);
  completedComplianceDocumentCount = computed(() => this.complianceDocumentRows().filter(row => row.required && row.uploads.length > 0).length);
  missingComplianceDocumentRows = computed(() => this.complianceDocumentRows().filter(row => row.required && row.uploads.length === 0));
  quickCreateContactDraft = computed<Customer>(() => {
    return {
      name: '',
      emailAddress: '',
      phoneNumber: '',
      corporateRole: 'Customer contact',
      address: this.address()?.street1 || '',
      company: this.quickCreateContactCompany() || undefined,
      countryId: this.address()?.countryId || this.client()?.countryId || this.quickCreateContactCompany()?.countryId || 0,
      roles: []
    } as unknown as Customer;
  });

  showNoteDialog = signal(false);
  showReminderDialog = signal(false);
  showCommDialog = signal(false);
  showMasterCommDialog = signal(false);
  showCustomerQuickCreateDialog = signal(false);
  showDocumentDialog = signal(false);
  showRegistrationAttachmentDialog = signal(false);

  newNoteText = signal('');
  newReminder = signal<CrmReminder>({ dueDate: new Date(), reminderText: '', completed: false, assignedUserId: 0, companyId: 0 });
  newCommLog = signal<CrmCommunicationLog>({ type: 'CALL', communicationDate: new Date(), summary: '', status: 'COMPLETED', companyId: 0 });
  masterCommunication = signal<CrmClientCommunication>({ communicationType: 'OPERATION', primaryRecord: false });
  documentDraft = signal<CrmClientDocument>({ category: 'GENERAL', title: '', expiryDate: undefined });
  registrationAttachmentDraft = signal<Partial<CrmClientRegistrationAttachment>>({ category: 'VAT_CERTIFICATE' });
  registrationAttachmentTypes = CRM_REGISTRATION_ATTACHMENT_TYPES;

  documentCategories = CRM_CLIENT_DOCUMENT_TYPES;

  ngOnInit() {
    const id = Number(this.route.snapshot.params['id']);
    this.route.queryParamMap.subscribe(queryParams => {
      this.activeTabValue = this.resolveTabValue(queryParams.get('tab'));
    });
    if (id) {
      this.loadClient(id);
    }
  }

  onTabChange(value: string | number) {
    this.activeTabValue = Number(value);
    const tabKey = this.tabKeys[this.activeTabValue];
    if (!tabKey) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabKey },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private resolveTabValue(tabKey: string | null): number {
    if (!tabKey) {
      return 0;
    }
    const normalized = tabKey.toLowerCase() === 'communication' ? 'contacts' : tabKey.toLowerCase();
    const tabIndex = this.tabKeys.findIndex(key => key === normalized);
    return tabIndex >= 0 ? tabIndex : 0;
  }

  loadClient(id: number) {
    forkJoin({
      client: this.crmService.getClient(id),
      address: this.crmService.getClientAddress(id),
      masterCommunications: this.crmService.getClientMasterCommunications(id),
      documents: this.crmService.getClientDocuments(id),
      registrationAttachments: this.crmService.getClientRegistrationAttachments(id),
      activities: this.crmService.getClientActivityTimeline(id),
      notes: this.crmService.getNotesByClient(id),
      logs: this.crmService.getCommunicationLogsByClient(id),
      reminders: this.crmService.getRemindersByClient(id),
      contractContext: this.contractsService.getCrmContextForClient(id),
      creditControl: this.crmService.getClientCreditControl(id).pipe(catchError(() => of(null)))
    }).subscribe(({ client, address, masterCommunications, documents, registrationAttachments, activities, notes, logs, reminders, contractContext, creditControl }) => {
      if (client.success && client.data) {
        this.client.set(client.data);
        this.clientLogoLoadFailed.set(false);
        this.loadLinkedCompany(client.data.linkedCompanyId);
        this.loadClientAccounts(client.data);
        this.loadContactDirectory(client.data);
        this.breadcrumbItems.set([
          { label: 'CRM', routerLink: '/crm/clients' },
          { label: 'Clients', routerLink: '/crm/clients' },
          { label: client.data.companyName || 'Client Details' }
        ]);
      } else {
        this.linkedCompany.set(null);
        this.clientAccounts.set([]);
        this.clientAccountManagers.set([]);
        this.contactDirectory.set([]);
      }
      this.address.set(address);
      this.masterCommunications.set(masterCommunications || []);
      this.documents.set(documents || []);
      this.registrationAttachments.set(registrationAttachments || []);
      this.activities.set(activities || []);
      this.notes.set(notes || []);
      this.communications.set(logs || []);
      this.reminders.set(reminders || []);
      this.contractContext.set(contractContext || null);
      this.creditControl.set(creditControl || null);
    });
  }

  reload() {
    const activeClient = this.client();
    if (activeClient?.id) this.loadClient(activeClient.id);
  }

  onClientLogoError(): void {
    this.clientLogoLoadFailed.set(true);
  }

  canApproveClient(client: CrmClient | null = this.client()): boolean {
    return client?.approvalStatus !== 'APPROVED'
      && this.permissionService.hasPermission(app_permissions.crm.clients.approve);
  }

  approveClient(): void {
    const activeClient = this.client();
    if (!activeClient?.id || !this.canApproveClient(activeClient) || this.approvingClient()) {
      return;
    }
    this.confirmationService.confirm({
      header: 'Approve CRM Client',
      message: `Confirm finance approval for <b>${activeClient.companyName}</b>?`,
      icon: 'pi pi-check-circle',
      acceptLabel: 'Approve',
      rejectLabel: 'Cancel',
      accept: () => this.performApproval(activeClient.id!)
    });
  }

  private performApproval(clientId: number): void {
    this.approvingClient.set(true);
    this.crmService.approveClient(clientId).subscribe({
      next: response => {
        this.approvingClient.set(false);
        if (response.success && response.data) {
          this.client.set(response.data);
          this.messageService.add({
            severity: 'success',
            summary: 'Client Approved',
            detail: 'Finance approval has been recorded.'
          });
        }
      },
      error: error => {
        this.approvingClient.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Approval Failed',
          detail: error?.error?.message || 'The client could not be approved.'
        });
      }
    });
  }

  approvalStatusLabel(status?: string | null): string {
    return status === 'APPROVED' ? 'Approved' : 'Draft – Awaiting Finance';
  }

  approvalSeverity(status?: string | null): 'success' | 'warn' {
    return status === 'APPROVED' ? 'success' : 'warn';
  }

  private loadLinkedCompany(linkedCompanyId?: number): void {
    if (!linkedCompanyId) {
      this.linkedCompany.set(null);
      return;
    }

    this.companyService.getCompanyById(linkedCompanyId).subscribe({
      next: response => this.linkedCompany.set(response?.data ?? null),
      error: () => this.linkedCompany.set(null)
    });
  }

  private loadClientAccounts(activeClient: CrmClient): void {
    if (!activeClient.id) {
      this.clientAccounts.set([]);
      this.clientAccountManagers.set([]);
      return;
    }

    forkJoin({
      accounts: this.crmService.getAccounts(activeClient.companyId, activeClient.id),
      managers: activeClient.linkedCompanyId
        ? this.customerService.getCustomersByCompany(activeClient.linkedCompanyId)
        : of([] as Customer[])
    }).subscribe({
      next: ({ accounts, managers }) => {
        this.clientAccounts.set(accounts || []);
        this.clientAccountManagers.set(managers || []);
      },
      error: () => {
        this.clientAccounts.set([]);
        this.clientAccountManagers.set([]);
      }
    });
  }

  private loadContactDirectory(activeClient: CrmClient): void {
    if (!activeClient.id) {
      this.contactDirectory.set([]);
      return;
    }
    this.crmService.getClientContactDirectory(activeClient.id, activeClient.companyId).subscribe({
      next: contacts => this.contactDirectory.set(contacts || []),
      error: () => this.contactDirectory.set([])
    });
  }

  saveNote() {
    const activeClient = this.client();
    if (!activeClient) return;
    const note: CrmNote = {
      clientId: activeClient.id,
      text: this.newNoteText(),
      companyId: activeClient.companyId,
      createdByUserId: this.toolbarService.customer()?.id || 0
    };
    this.crmService.saveNote(note).subscribe(res => {
      if (res.success) {
        this.showNoteDialog.set(false);
        this.newNoteText.set('');
        this.reload();
      }
    });
  }

  saveReminder() {
    const activeClient = this.client();
    if (!activeClient) return;
    const reminder = { ...this.newReminder(), clientId: activeClient.id, companyId: activeClient.companyId, assignedUserId: this.toolbarService.customer()?.id || 0 };
    this.crmService.saveReminder(reminder).subscribe(res => {
      if (res.success) {
        this.showReminderDialog.set(false);
        this.reload();
      }
    });
  }

  saveCommLog() {
    const activeClient = this.client();
    if (!activeClient) return;
    const log = { ...this.newCommLog(), clientId: activeClient.id, companyId: activeClient.companyId, createdByUserId: this.toolbarService.customer()?.id || 0 };
    this.crmService.saveCommunicationLog(log).subscribe(res => {
      if (res.success) {
        this.showCommDialog.set(false);
        this.reload();
      }
    });
  }

  saveMasterCommunication() {
    const activeClient = this.client();
    if (!activeClient?.id) return;
    this.crmService.saveClientMasterCommunication(activeClient.id, this.masterCommunication(), activeClient.companyId).subscribe(res => {
      if (res.success) {
        this.showMasterCommDialog.set(false);
        this.resetMasterCommunicationDraft();
        this.reload();
      }
    });
  }

  startAddMasterCommunication() {
    this.resetMasterCommunicationDraft();
    this.showCustomerQuickCreateDialog.set(true);
  }

  editMasterCommunication(row: CrmClientCommunication) {
    this.masterCommunication.set({ ...row });
    this.showMasterCommDialog.set(true);
  }

  onCustomerQuickCreateCompleted(result: PartyQuickCreateResult): void {
    const activeClient = this.client();
    if (!activeClient?.id) {
      return;
    }
    const customer = result.record as Customer;

    const existingPrimary = this.masterCommunications().some(row => row.primaryRecord);
    const communication = this.customerToClientCommunication(
      customer,
      existingPrimary ? false : true
    );

    this.crmService.saveClientMasterCommunication(activeClient.id, communication, activeClient.companyId).subscribe(response => {
      if (response.success) {
        this.showCustomerQuickCreateDialog.set(false);
        this.reload();
      }
    });
  }

  onCustomerQuickCreateFailed(message: string): void {
    this.messageService.add({ severity: 'error', summary: 'Contact Create Failed', detail: message || 'Unable to save customer contact' });
  }

  createQuotation() {
    const activeClient = this.client();
    if (!activeClient?.id) return;
    this.router.navigate(['/crm/quotations/new'], {
      queryParams: {
        sourceType: 'CLIENT',
        sourceId: activeClient.id
      }
    });
  }

  deleteMasterCommunication(row: CrmClientCommunication) {
    const activeClient = this.client();
    if (!activeClient?.id || !row.id) return;
    this.crmService.deleteClientMasterCommunication(activeClient.id, row.id, activeClient.companyId).subscribe(() => this.reload());
  }

  primaryMasterCommunication(): CrmClientCommunication | null {
    return this.masterCommunications().find(row => row.primaryRecord) || this.masterCommunications()[0] || null;
  }

  accountContacts(account: CrmAccount): CrmClientContactDirectoryEntry[] {
    if (!account.id) return [];
    return this.contactDirectory().filter(contact => (contact.accounts || []).some(item => item.id === account.id));
  }

  clientAccountKamName(account: CrmAccount): string {
    return this.clientAccountManagers().find(customer => customer.id === account.kamCustomerId)?.name || 'Not assigned';
  }

  editDirectoryContact(entry: CrmClientContactDirectoryEntry): void {
    if (!entry.clientContact || !entry.crmClientContactId) return;
    const communication = this.masterCommunications().find(item => item.id === entry.crmClientContactId);
    if (communication) this.editMasterCommunication(communication);
  }

  deleteDirectoryContact(entry: CrmClientContactDirectoryEntry): void {
    if (!entry.clientContact || !entry.crmClientContactId) return;
    const communication = this.masterCommunications().find(item => item.id === entry.crmClientContactId);
    if (communication) this.deleteMasterCommunication(communication);
  }

  communicationTypeLabel(type?: string | null): string {
    return (type || 'GENERAL').replaceAll('_', ' ');
  }

  createAgreement() {
    const activeClient = this.client();
    if (!activeClient?.id) return;
    const context = this.contractContext();
    const queryParams: Record<string, number> = { crmClientId: activeClient.id };
    if (context?.accounts?.length === 1 && context.accounts[0].id) {
      queryParams['crmAccountId'] = context.accounts[0].id;
    }
    if (activeClient.sourceLeadId) {
      queryParams['sourceLeadId'] = activeClient.sourceLeadId;
    }
    if (activeClient.kamCustomerId) {
      queryParams['kamCustomerId'] = activeClient.kamCustomerId;
    }
    this.router.navigate(['/contracts/create'], { queryParams });
  }

  getContractHealthFlags(): string[] {
    return this.contractContext()?.healthFlags ?? [];
  }

  uploadDocument(event: Event) {
    const activeClient = this.client();
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!activeClient?.id || !file) return;

    this.uploadingDocument.set(true);
    this.firebaseStorageService.uploadFileAndGetPublicUrl(file, `crm/client-documents/${activeClient.id}/${Date.now()}-${file.name}`).subscribe({
      next: fileUrl => {
        const draft = this.documentDraft();
        const document: CrmClientDocument = {
          ...draft,
          title: draft.title || file.name,
          fileName: file.name,
          fileUrl,
          fileType: file.type,
          fileSize: file.size,
          uploadedByCustomerId: this.toolbarService.customer()?.id
        };
        this.crmService.saveClientDocument(activeClient.id!, document, activeClient.companyId).subscribe(response => {
          this.uploadingDocument.set(false);
          input.value = '';
          if (response.success) {
            this.showDocumentDialog.set(false);
            this.documentDraft.set({ category: 'GENERAL', title: '', expiryDate: undefined });
            this.reload();
          }
        });
      },
      error: () => {
        this.uploadingDocument.set(false);
        input.value = '';
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload document' });
      }
    });
  }

  uploadRegistrationAttachment(event: Event) {
    const activeClient = this.client();
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!activeClient?.id || !file) return;

    this.uploadingDocument.set(true);
    this.firebaseStorageService.uploadFileAndGetPublicUrl(file, `crm/client-registration/${activeClient.id}/${Date.now()}-${file.name}`).subscribe({
      next: fileUrl => {
        const attachment: CrmClientRegistrationAttachment = {
          category: this.registrationAttachmentDraft().category || 'VAT_CERTIFICATE',
          fileName: file.name,
          fileUrl,
          fileType: file.type,
          fileSize: file.size,
          uploadedByCustomerId: this.toolbarService.customer()?.id
        };
        this.crmService.saveClientRegistrationAttachment(activeClient.id!, attachment, activeClient.companyId).subscribe(response => {
          this.uploadingDocument.set(false);
          input.value = '';
          if (response.success) {
            this.showRegistrationAttachmentDialog.set(false);
            this.registrationAttachmentDraft.set({ category: 'VAT_CERTIFICATE' });
            this.reload();
          }
        });
      },
      error: () => {
        this.uploadingDocument.set(false);
        input.value = '';
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload registration attachment' });
      }
    });
  }

  deleteRegistrationAttachment(attachment: CrmClientRegistrationAttachment) {
    const activeClient = this.client();
    if (!activeClient?.id || !attachment.id) return;
    this.crmService.deleteClientRegistrationAttachment(activeClient.id, attachment.id, activeClient.companyId).subscribe(() => this.reload());
  }

  deleteDocument(document: CrmClientDocument) {
    const activeClient = this.client();
    if (!activeClient?.id || !document.id) return;
    this.crmService.deleteClientDocument(activeClient.id, document.id, activeClient.companyId).subscribe(() => this.reload());
  }

  updateCommType(type: string) {
    this.newCommLog.update(value => ({ ...value, type }));
  }

  updateCommLogSummary(summary: string) {
    this.newCommLog.update(value => ({ ...value, summary }));
  }

  updateCommLogDate(communicationDate: Date) {
    this.newCommLog.update(value => ({ ...value, communicationDate }));
  }

  updateReminderText(reminderText: string) {
    this.newReminder.update(value => ({ ...value, reminderText }));
  }

  updateReminderDueDate(dueDate: Date) {
    this.newReminder.update(value => ({ ...value, dueDate }));
  }

  updateMasterCommunicationField(field: keyof CrmClientCommunication, value: string | boolean | undefined) {
    this.masterCommunication.update(communication => ({ ...communication, [field]: value }));
  }

  private resetMasterCommunicationDraft() {
    this.masterCommunication.set({ communicationType: 'OPERATION', primaryRecord: false });
  }

  private customerToClientCommunication(customer: Customer, primaryRecord: boolean): CrmClientCommunication {
    const current = this.masterCommunication();
    const activeClient = this.client();
    return {
      id: current.id,
      clientId: activeClient?.id,
      companyId: activeClient?.companyId,
      customerId: customer.id,
      communicationType: current.id ? current.communicationType || 'CUSTOMER' : 'CUSTOMER',
      name: customer.name?.trim() || current.name || '',
      email: customer.emailAddress?.trim() || current.email || '',
      phone: customer.phoneNumber?.trim() || current.phone || '',
      mobile: current.mobile || '',
      extension: current.extension || '',
      fax: current.fax || '',
      website: current.website || '',
      primaryRecord: primaryRecord || current.primaryRecord || false
    };
  }

  updateDocumentDraftField(field: keyof CrmClientDocument, value: string | Date | undefined) {
    this.documentDraft.update(document => ({ ...document, [field]: value }));
  }

  updateRegistrationAttachmentCategory(category: CrmClientRegistrationAttachment['category']) {
    this.registrationAttachmentDraft.update(attachment => ({ ...attachment, category }));
  }

  startUploadForComplianceDocument(row: ClientComplianceDocumentRow) {
    if (row.source === 'registration') {
      this.registrationAttachmentDraft.set({ category: row.key as CrmClientRegistrationAttachment['category'] });
      this.showRegistrationAttachmentDialog.set(true);
      return;
    }

    this.documentDraft.set({ category: row.key, title: row.label, expiryDate: undefined });
    this.showDocumentDialog.set(true);
  }

  registrationAttachmentLabel(category?: string): string {
    return this.registrationAttachmentTypes.find(type => type.value === category)?.label || category || '-';
  }

  documentCategoryLabel(category?: string): string {
    return this.documentCategories.find(type => type.value === category)?.label || category || '-';
  }

  getCategorySeverity(category: string) {
    switch (category) {
      case 'VIP': return 'success';
      case 'PREMIUM': return 'warn';
      case 'PARTNER': return 'info';
      default: return 'secondary';
    }
  }

  roleLabel(role?: string | null): string {
    return crmRoleLabel(role);
  }

  roleSummary(client: CrmClient | null = this.client()): string {
    const roles = client?.roles || [];
    return roles.length ? roles.map(role => this.roleLabel(role)).join(', ') : 'Roles pending classification';
  }

  linkedCompanyDisplayName(): string {
    return this.linkedCompany()?.name || 'Linked company';
  }

  linkedCustomerDisplayName(client: CrmClient | null = this.client()): string {
    if (!client?.linkedCustomerId) {
      return '-';
    }
    return client.companyName || client.customerNameLocal || 'Linked customer profile';
  }

  clientAccountManagerNames(account: CrmAccount): string[] {
    return (account.clientAccountManagerCustomerIds || []).map(managerId =>
      this.clientAccountManagers().find(customer => customer.id === managerId)?.name || `Customer #${managerId}`
    );
  }

  isCustomerClient(client: CrmClient | null = this.client()): boolean {
    return (client?.roles || []).some(role => `${role || ''}`.toUpperCase() === 'CUSTOMER');
  }

  getCreditStatus(): string {
    return this.creditControl()?.creditStatus || 'CLEAR';
  }

  creditStatusLabel(status?: string | null): string {
    return (status || 'CLEAR').replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  }

  creditCheckModeLabel(mode?: string | null): string {
    switch ((mode || 'ENFORCE').toUpperCase()) {
      case 'WARN': return 'Warn Only';
      case 'DISABLED': return 'Disabled';
      default: return 'Enforce and Block';
    }
  }

  creditSeverity(status?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch ((status || '').toUpperCase()) {
      case 'CLEAR': return 'success';
      case 'WARNING': return 'warn';
      case 'BLOCKED': return 'danger';
      case 'OVERRIDE_APPROVED': return 'info';
      default: return 'secondary';
    }
  }

  creditMetricTone(status?: string | null): PageMetric['tone'] {
    switch ((status || '').toUpperCase()) {
      case 'CLEAR': return 'success';
      case 'WARNING': return 'warn';
      case 'BLOCKED': return 'danger';
      default: return 'info';
    }
  }

  getCreditWarnings(): string[] {
    return this.creditControl()?.warnings || [];
  }

  getCreditBlockers(): string[] {
    return this.creditControl()?.blockers || [];
  }

  getActivitySeverity(activityType: CrmActivityType) {
    switch (activityType) {
      case CrmActivityType.NOTE: return 'bg-blue-500';
      case CrmActivityType.REMINDER: return 'bg-orange-500';
      case CrmActivityType.COMMUNICATION:
      default: return 'bg-purple-500';
    }
  }

  getActivityIcon(activityType: CrmActivityType) {
    switch (activityType) {
      case CrmActivityType.NOTE: return 'pi pi-pencil';
      case CrmActivityType.REMINDER: return 'pi pi-calendar';
      case CrmActivityType.COMMUNICATION:
      default: return 'pi pi-comments';
    }
  }
}
